import { useAtom } from 'jotai';
import { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { userAtom, currentPathAtom } from '../../app/atoms';
import { paths } from '../../config/paths';

export const Layout = ({ children }: { children: React.ReactNode }) => {
    const [user] = useAtom(userAtom);
    const [currentPath, setCurrentPath] = useAtom(currentPathAtom);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setCurrentPath(location.pathname);
        setIsMobileMenuOpen(false);
    }, [location.pathname, setCurrentPath]);

    const getIsActive = useMemo(() => {
        return (id: number, path: string) => {
            if (id === 1) {
                return currentPath === paths.buyCard.path || currentPath === paths.buyCardChecking.path;
            }
            return currentPath === path;
        };
    }, [currentPath]);

    const menuItems = useMemo(() => [
        {
            id: 1,
            label: 'Buy Fujiyama Tap Debit Card',
            path: paths.buyCard.path,
            isDisabled: user?.is_debit_card_purchased,
            show: true
        },
        {
            id: 2,
            label: 'Main Menu',
            path: paths.dashboard.path,
            isDisabled: !user?.is_debit_card_purchased,
            show: true
        },
        {
            id: 3,
            label: 'Check Children',
            path: paths.checkChildren.path,
            isDisabled: false,
            show: user?.is_debit_card_purchased
        },
        {
            id: 11,
            label: 'Referral Tree',
            path: paths.referralTree.path,
            isDisabled: false,
            show: user?.is_debit_card_purchased
        },
        {
            id: 4,
            label: '[Admin] Debit Card Requests',
            path: paths.admin.cardRequests.path,
            isDisabled: false,
            show: user?.has_admin_role
        },
        {
            id: 5,
            label: '[Admin] Withdraw Requests',
            path: paths.admin.withdrawRequests.path,
            isDisabled: false,
            show: user?.has_admin_role
        },
        {
            id: 6,
            label: '[Admin] Users Summary',
            path: paths.admin.usersSummary.path,
            isDisabled: false,
            show: user?.has_admin_role
        },
        {
            id: 10,
            label: '[Admin] Users Search',
            path: paths.admin.usersSearch.path,
            isDisabled: false,
            show: user?.has_admin_role
        },
        {
            id: 7,
            label: '[Admin] Debit Card Delivery',
            path: paths.admin.cardDelivery.path,
            isDisabled: false,
            show: user?.has_admin_role || user?.has_card_delivery_check_role
        },
        {
            id: 8,
            label: '[Admin] Fix Referrals',
            path: paths.admin.fixReferrals.path,
            isDisabled: false,
            show: user?.has_admin_role
        },
        {
            id: 9,
            label: '[Admin] Tapbot Bulk Insert',
            path: paths.admin.tapbotBulkInsert.path,
            isDisabled: false,
            show: user?.has_admin_role
        }
    ], [user]);

    return (
        <div className="flex h-screen">
            {/* モバイルメニューボタン */}
            <button
                className="fixed top-4 left-4 z-60 md:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
                <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    {isMobileMenuOpen ? (
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    ) : (
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 12h16M4 18h16"
                        />
                    )}
                </svg>
            </button>

            {/* サイドメニュー */}
            <div
                className={`fixed md:static w-80 h-full bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
                    }`}
            >
                <div className="p-4">
                    <h2 className="text-xl font-bold mb-6">Fujiyama Debit Card Manager</h2>
                    <nav>
                        <ul className="space-y-2">
                            {menuItems.map((item) => {
                                if (!item.show) return null;
                                return item.isDisabled ? (
                                    <li key={item.id}>
                                        <div className="block p-2 rounded-lg transition-colors bg-gray-100 text-gray-400 cursor-not-allowed">
                                            {item.label}
                                        </div>
                                    </li>
                                ) : (
                                    <li key={item.id}>
                                        <Link
                                            to={item.path}
                                            className={`block p-2 rounded-lg transition-colors ${getIsActive(item.id, item.path)
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'hover:bg-blue-50 text-blue-600 hover:text-blue-700'
                                                }`}
                                        >
                                            {item.label}
                                        </Link>
                                    </li>
                                );
                            }
                            )}
                        </ul>
                    </nav>
                </div>
            </div>

            {/* メインコンテンツ */}
            <div className="flex-1 overflow-auto">
                <div className="p-4 md:p-8">
                    {children}
                </div>
            </div>

            {/* モバイルメニューのオーバーレイ */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}
        </div>
    );
};

export default Layout;